import React, { useState, useEffect, useCallback, useRef } from 'react'
import { supabase } from './supabase'
import { Auth } from '@supabase/auth-ui-react'
import { ThemeSupa } from '@supabase/auth-ui-shared'

const RED = '#c41e3a'
const API_BASE = 'https://giving-creation-production-d2fa.up.railway.app'
const ALLOWED_DOMAIN = 'ezfloorz.com'
const STAGES = ['Prospecting','Accepted','Contacted','Qualifying','Measure','Appointment','Estimating','Quoted','Negotiations','On Hold','Sold - Job in Progress','Service Needed','Sold - Materials on Order','Sold - Job Scheduled','Sold - Job Completion Follow Up','Completed Sale','No Sale','Dead']
// Stages that count as a WON sale
const WON_STAGES = ['Sold - Job in Progress','Sold - Materials on Order','Sold - Job Scheduled','Sold - Job Completion Follow Up','Completed Sale']
// Stages that mean the lead is lost
const LOST_STAGES = ['No Sale','Dead']
// Reasons for marking a lead lost
const LOST_REASONS = ['Competitor - Exclusive Product','Competitor - Install Deadline','Competitor - Price','Competitor - Product Availability','Competitor - Selection','Competitor - Service','No Reason Given','No Reply - 6 months Dead','Poor Follow Up']
// Reasons for marking a lead unqualified (does not affect rep KPIs)
const UNQUALIFIED_REASONS = ['Spam / Fake Lead','Duplicate','Wrong Number','Out of Area','Unqualified - Custom Project','Unqualified - Hardwood Refinish','Unqualified - Service Related','Unqualified - Tile Project']
const REPS   = ['Nick Olberding','Tyler Ploog','Nicole Ahrendt','Hailey Wade']
const SOURCES = ['Website','Word of Mouth','Google Ad','Nextdoor','Client Referral','Repeat Business','Facebook','Walk-In','Phone','Other']
// Stages that mark a lead as fully CLOSED (removed from open view).
// The 4 in-progress 'Sold' stages stay OPEN — work is ongoing — even though
// they count as won revenue. Only 'Completed Sale' closes a won lead.
const CLOSED_STAGES = ['Completed Sale', ...LOST_STAGES]

const STAGE_COLORS = {
  'Prospecting':                    { bg:'#e6f1fb', color:'#185fa5' },
  'Accepted':                       { bg:'#e6f1fb', color:'#185fa5' },
  'Contacted':                      { bg:'#eeedfe', color:'#3c3489' },
  'Qualifying':                     { bg:'#e6f1fb', color:'#185fa5' },
  'Measure':                        { bg:'#fbeaf0', color:'#72243e' },
  'Appointment':                    { bg:'#fbeaf0', color:'#72243e' },
  'Estimating':                     { bg:'#fcebeb', color:'#791f1f' },
  'Quoted':                         { bg:'#eaf3de', color:'#3b6d11' },
  'Negotiations':                   { bg:'#eaf3de', color:'#3b6d11' },
  'On Hold':                        { bg:'#faeeda', color:'#854f0b' },
  'Sold - Job in Progress':         { bg:'#e1f5ee', color:'#0f6e56' },
  'Service Needed':                 { bg:'#faeeda', color:'#854f0b' },
  'Sold - Materials on Order':      { bg:'#e1f5ee', color:'#0f6e56' },
  'Sold - Job Scheduled':           { bg:'#e1f5ee', color:'#0f6e56' },
  'Sold - Job Completion Follow Up':{ bg:'#e1f5ee', color:'#0f6e56' },
  'Completed Sale':                 { bg:'#d1f0e4', color:'#0a5544' },
  'No Sale':                        { bg:'#fcebeb', color:'#791f1f' },
  'Dead':                           { bg:'#f4f4f5', color:'#71717a' },
}

// filterType: 'text' | 'select' | 'created' | 'taskdate' | 'none'
const ALL_COLS = [
  { key:'created_at',    label:'Created',         sortable:true,  filterType:'created',  defaultVisible:true  },
  { key:'updated_at',    label:'Updated',         sortable:true,  filterType:'none',     defaultVisible:true  },
  { key:'pipeline',      label:'Pipeline',        sortable:true,  filterType:'select',   defaultVisible:true  },
  { key:'assigned_to',   label:'Assigned To',     sortable:true,  filterType:'select',   defaultVisible:true  },
  { key:'stage',         label:'Stage',           sortable:true,  filterType:'select',   defaultVisible:true  },
  { key:'task',          label:'Task',            sortable:false, filterType:'text',     defaultVisible:true  },
  { key:'task_date',     label:'Task Date',       sortable:true,  filterType:'taskdate', defaultVisible:true  },
  { key:'company',       label:'Company',         sortable:true,  filterType:'text',     defaultVisible:true  },
  { key:'first_name',    label:'First',           sortable:true,  filterType:'text',     defaultVisible:true  },
  { key:'last_name',     label:'Last',            sortable:true,  filterType:'text',     defaultVisible:true  },
  { key:'phone',         label:'Phone',           sortable:false, filterType:'text',     defaultVisible:true  },
  { key:'email',         label:'Email',           sortable:false, filterType:'text',     defaultVisible:true  },
  { key:'how_heard',     label:'How Heard',       sortable:true,  filterType:'select',   defaultVisible:true  },
  { key:'quote_amount',  label:'Quote',           sortable:true,  filterType:'none',     defaultVisible:true  },
  { key:'products',      label:'Products',        sortable:false, filterType:'text',     defaultVisible:true  },
  { key:'timeToContact', label:'Time to Contact', sortable:true,  filterType:'none',     defaultVisible:true  },
  { key:'timeToClose',   label:'Time to Close',   sortable:true,  filterType:'none',     defaultVisible:true  },
  { key:'order_number',  label:'Order #',         sortable:false, filterType:'text',     defaultVisible:true  },
  { key:'source',        label:'Lead Source',     sortable:true,  filterType:'select',   defaultVisible:false },
  { key:'city',          label:'City',            sortable:true,  filterType:'text',     defaultVisible:false },
]

function daysDiff(a,b){ if(!a||!b) return null; return Math.round((new Date(b)-new Date(a))/(1000*60*60*24)) }
function fmtDate(str){ if(!str) return '—'; const d=new Date(str); return `${d.getMonth()+1}/${d.getDate()}/${String(d.getFullYear()).slice(2)}` }
function fmtMoney(n){ return n?'$'+Number(n).toLocaleString():'—' }
function timeBadge(days,type){
  if(days===null||days===undefined) return null
  const [fast,slow]=type==='contact'?[2,5]:[30,60]
  const bg=days<=fast?'#eaf3de':days<=slow?'#faeeda':'#fcebeb'
  const color=days<=fast?'#3b6d11':days<=slow?'#854f0b':'#791f1f'
  return {label:`${days}d`,bg,color}
}
function startOfToday(){ const d=new Date(); d.setHours(0,0,0,0); return d }
function isPastDue(dateStr){ if(!dateStr) return false; const td=new Date(dateStr); td.setHours(0,0,0,0); return td<startOfToday() }
function isDueToday(dateStr){ if(!dateStr) return false; const td=new Date(dateStr); td.setHours(0,0,0,0); return td.getTime()===startOfToday().getTime() }

function LoginScreen(){
  return(
    <div style={{minHeight:'100vh',display:'flex',alignItems:'center',justifyContent:'center',background:'#f4f4f5'}}>
      <div style={{background:'#fff',borderRadius:12,overflow:'hidden',width:400,boxShadow:'0 4px 24px rgba(0,0,0,.08)'}}>
        <div style={{background:RED,padding:'28px 32px'}}>
          <div style={{color:'#fff',fontSize:22,fontWeight:700,marginBottom:4}}>EZ Floorz CRM</div>
          <div style={{color:'rgba(255,255,255,.75)',fontSize:13}}>Sign in with your @ezfloorz.com account</div>
        </div>
        <div style={{padding:'24px 32px 32px'}}>
          <Auth supabaseClient={supabase}
            appearance={{theme:ThemeSupa,variables:{default:{colors:{brand:RED,brandAccent:'#a51930'}}}}}
            providers={['google']} onlyThirdPartyProviders={true} redirectTo={window.location.origin}/>
        </div>
      </div>
    </div>
  )
}

function ColPicker({visible,onChange,onClose,order,onSave,onReset,savedMsg}){
  const ref=useRef()
  useEffect(()=>{
    function h(e){if(ref.current&&!ref.current.contains(e.target)) onClose()}
    document.addEventListener('mousedown',h)
    return()=>document.removeEventListener('mousedown',h)
  },[onClose])
  const ordered=order.map(k=>ALL_COLS.find(c=>c.key===k)).filter(Boolean)
  return(
    <div ref={ref} style={{position:'absolute',top:36,right:0,background:'#fff',border:'1px solid #e4e4e7',borderRadius:8,padding:12,zIndex:50,width:250,boxShadow:'0 4px 16px rgba(0,0,0,.12)',maxHeight:420,overflowY:'auto'}}>
      <div style={{fontSize:11,fontWeight:600,color:'#71717a',textTransform:'uppercase',letterSpacing:.5,marginBottom:4}}>Show / Hide Columns</div>
      <div style={{fontSize:11,color:'#a1a1aa',marginBottom:8,lineHeight:1.4}}>Drag column headers in the table to reorder.</div>
      {ordered.map(c=>(
        <label key={c.key} style={{display:'flex',alignItems:'center',gap:8,padding:'4px 0',cursor:'pointer',fontSize:13}}>
          <input type="checkbox" checked={visible.includes(c.key)} onChange={()=>{
            const next=visible.includes(c.key)?visible.filter(x=>x!==c.key):[...visible,c.key]
            onChange(next)
          }}/>
          {c.label}
        </label>
      ))}
      <div style={{borderTop:'1px solid #e4e4e7',marginTop:10,paddingTop:10,display:'flex',gap:6,alignItems:'center'}}>
        <button onClick={onSave} style={{flex:1,padding:'6px 10px',background:RED,color:'#fff',border:'none',borderRadius:6,fontSize:12,fontWeight:500,cursor:'pointer',fontFamily:'Inter,sans-serif'}}>Save layout</button>
        <button onClick={onReset} style={{padding:'6px 10px',background:'#fff',color:'#71717a',border:'1px solid #e4e4e7',borderRadius:6,fontSize:12,cursor:'pointer',fontFamily:'Inter,sans-serif'}}>Reset</button>
      </div>
      {savedMsg&&<div style={{fontSize:11,color:RED,marginTop:6,textAlign:'center',fontWeight:500}}>{savedMsg}</div>}
    </div>
  )
}

export default function App(){
  const [session,setSession]=useState(null)
  const [loading,setLoading]=useState(true)
  const [leads,setLeads]=useState([])
  const [activities,setActivities]=useState({})
  const [view,setView]=useState('open')
  const [searchQ,setSearchQ]=useState('')
  const [colFilters,setColFilters]=useState({}) // { colKey: value }
  const [sortCol,setSortCol]=useState('created_at')
  const [sortDir,setSortDir]=useState(-1)
  const [activeId,setActiveId]=useState(null)
  const [showNewLead,setShowNewLead]=useState(false)
  const [showColPicker,setShowColPicker]=useState(false)
  const [visibleCols,setVisibleCols]=useState(ALL_COLS.filter(c=>c.defaultVisible).map(c=>c.key))
  const [colOrder,setColOrder]=useState(ALL_COLS.map(c=>c.key))
  const [dragCol,setDragCol]=useState(null)
  const [dragOverCol,setDragOverCol]=useState(null)
  const [savedMsg,setSavedMsg]=useState('')
  const [closeReasonMode,setCloseReasonMode]=useState(null) // 'lost' | 'unqualified' | null

  useEffect(()=>{
    supabase.auth.getSession().then(({data:{session}})=>{setSession(session);setLoading(false)})
    const {data:{subscription}}=supabase.auth.onAuthStateChange((_,s)=>setSession(s))
    return()=>subscription.unsubscribe()
  },[])

  const loadLeads=useCallback(async()=>{
    const {data,error}=await supabase.from('leads').select('*').order('created_at',{ascending:false})
    if(!error) setLeads(data||[])
  },[])

  useEffect(()=>{if(session) loadLeads()},[session,loadLeads])

  // Load saved column layout for this user
  useEffect(()=>{
    if(!session?.user?.email) return
    try{
      const raw=window.localStorage.getItem('ezfloorz_cols_'+session.user.email)
      if(raw){
        const saved=JSON.parse(raw)
        if(Array.isArray(saved.visible)&&saved.visible.length) setVisibleCols(saved.visible.filter(k=>ALL_COLS.some(c=>c.key===k)))
        if(Array.isArray(saved.order)&&saved.order.length){
          const valid=saved.order.filter(k=>ALL_COLS.some(c=>c.key===k))
          const missing=ALL_COLS.map(c=>c.key).filter(k=>!valid.includes(k))
          setColOrder([...valid,...missing])
        }
      }
    }catch(e){ console.warn('Could not load column prefs',e) }
  },[session])

  useEffect(()=>{
    if(!activeId) return
    supabase.from('activities').select('*').eq('lead_id',activeId).order('created_at',{ascending:false})
      .then(({data})=>{if(data) setActivities(p=>({...p,[activeId]:data}))})
  },[activeId])

  if(loading) return <div style={{padding:40,textAlign:'center',color:'#71717a'}}>Loading…</div>
  if(!session) return <LoginScreen/>

  const email=session.user?.email||''
  if(!email.endsWith('@'+ALLOWED_DOMAIN)&&!email.endsWith('@gmail.com')){
    return(
      <div style={{minHeight:'100vh',display:'flex',alignItems:'center',justifyContent:'center'}}>
        <div style={{background:'#fff',borderRadius:10,padding:32,textAlign:'center',maxWidth:360}}>
          <div style={{fontSize:16,fontWeight:600,marginBottom:8}}>Access Denied</div>
          <div style={{fontSize:13,color:'#71717a',marginBottom:16}}>Only @ezfloorz.com accounts can access this CRM.</div>
          <button style={{padding:'7px 14px',background:RED,color:'#fff',border:'none',borderRadius:6,cursor:'pointer'}} onClick={()=>supabase.auth.signOut()}>Sign out</button>
        </div>
      </div>
    )
  }

  const activeLead=leads.find(l=>l.id===activeId)

  function setColFilter(key,val){ setColFilters(p=>({...p,[key]:val})) }
  function clearColFilters(){ setColFilters({}) }

  function matchCreatedFilter(dateStr,filterVal){
    if(!filterVal) return true
    if(!dateStr) return false
    const d=new Date(dateStr)
    const now=new Date()
    const cutoff=new Date()
    if(filterVal==='week')   cutoff.setDate(now.getDate()-7)
    if(filterVal==='month')  cutoff.setMonth(now.getMonth()-1)
    if(filterVal==='3month') cutoff.setMonth(now.getMonth()-3)
    return d>=cutoff
  }
  function matchTaskDateFilter(dateStr,filterVal){
    if(!filterVal) return true
    if(filterVal==='today')   return isDueToday(dateStr)
    if(filterVal==='overdue') return isPastDue(dateStr)
    return true
  }

  const visibleLeads=leads
    .filter(l=>{
      if(view==='open')   return !l.closed&&!CLOSED_STAGES.includes(l.stage)&&!l.unqualified
      if(view==='closed') return l.closed||CLOSED_STAGES.includes(l.stage)||l.unqualified
      return true
    })
    .filter(l=>{
      const q=searchQ.toLowerCase()
      if(q&&!`${l.first_name||''}${l.last_name||''}${l.email||''}${l.company||''}${l.products||''}${l.phone||''}`.toLowerCase().includes(q)) return false
      // per-column filters
      for(const col of ALL_COLS){
        const fv=colFilters[col.key]
        if(!fv) continue
        if(col.filterType==='text'){
          if(!String(l[col.key]||'').toLowerCase().includes(fv.toLowerCase())) return false
        } else if(col.filterType==='select'){
          if(String(l[col.key]||'')!==fv) return false
        } else if(col.filterType==='created'){
          if(!matchCreatedFilter(l.created_at,fv)) return false
        } else if(col.filterType==='taskdate'){
          if(!matchTaskDateFilter(l.task_date,fv)) return false
        }
      }
      return true
    })
    .map(l=>({...l,timeToContact:daysDiff(l.created_at,l.contacted_at),timeToClose:daysDiff(l.created_at,l.closed_at)}))
    .sort((a,b)=>{
      let av=a[sortCol],bv=b[sortCol]
      if(av===null||av===undefined) av=sortDir>0?'zzz':''
      if(bv===null||bv===undefined) bv=sortDir>0?'zzz':''
      return av<bv?sortDir:av>bv?-sortDir:0
    })

  // KPI base: respects column filters + search (but NOT the open/closed view toggle),
  // so stats reflect the filtered book of business — e.g. one rep's full numbers.
  const kpiBase=leads.filter(l=>{
    const q=searchQ.toLowerCase()
    if(q&&!`${l.first_name||''}${l.last_name||''}${l.email||''}${l.company||''}${l.products||''}${l.phone||''}`.toLowerCase().includes(q)) return false
    for(const col of ALL_COLS){
      const fv=colFilters[col.key]
      if(!fv) continue
      if(col.filterType==='text'){ if(!String(l[col.key]||'').toLowerCase().includes(fv.toLowerCase())) return false }
      else if(col.filterType==='select'){ if(String(l[col.key]||'')!==fv) return false }
      else if(col.filterType==='created'){ if(!matchCreatedFilter(l.created_at,fv)) return false }
      else if(col.filterType==='taskdate'){ if(!matchTaskDateFilter(l.task_date,fv)) return false }
    }
    return true
  })
  const anyFilterActive=Object.values(colFilters).filter(Boolean).length>0||!!searchQ
  // Unqualified leads are excluded from ALL performance stats
  const qualifiedBase=kpiBase.filter(l=>!l.unqualified)
  const openLeads=qualifiedBase.filter(l=>!l.closed&&!CLOSED_STAGES.includes(l.stage))
  const closedLeads=qualifiedBase.filter(l=>l.closed||CLOSED_STAGES.includes(l.stage))
  const wonLeads=qualifiedBase.filter(l=>WON_STAGES.includes(l.stage))
  const pipelineVal=openLeads.reduce((s,l)=>s+(l.quote_amount||0),0)
  const twelveMonthsAgo=new Date(); twelveMonthsAgo.setMonth(twelveMonthsAgo.getMonth()-12)
  const wonLast12=wonLeads.filter(l=>{const d=l.won_at||l.closed_at;return d&&new Date(d)>=twelveMonthsAgo})
  const wonVal=wonLast12.reduce((s,l)=>s+(l.quote_amount||0),0)
  const ttcArr=qualifiedBase.filter(l=>l.contacted_at).map(l=>daysDiff(l.created_at,l.contacted_at))
  const ttclArr=qualifiedBase.filter(l=>l.closed_at&&l.stage!=='Accepted'&&l.stage!=='Prospecting').map(l=>daysDiff(l.created_at,l.closed_at))
  const avg=arr=>arr.length?Math.round(arr.reduce((a,b)=>a+b,0)/arr.length):null
  const overdueCount=qualifiedBase.filter(l=>!l.closed&&isPastDue(l.task_date)).length
  const closeRate=qualifiedBase.length?Math.round(wonLeads.length/qualifiedBase.length*100):0

  function handleSort(col){
    if(sortCol===col) setSortDir(d=>d*-1)
    else{setSortCol(col);setSortDir(-1)}
  }
  function sortArrow(col){
    if(sortCol!==col) return <span style={{opacity:.25,marginLeft:3}}>↕</span>
    return <span style={{color:RED,marginLeft:3}}>{sortDir>0?'↑':'↓'}</span>
  }

  async function updateLead(id,updates){
    const payload={...updates,updated_at:new Date().toISOString()}
    if(updates.stage&&!CLOSED_STAGES.includes(updates.stage)){
      const lead=leads.find(l=>l.id===id)
      if(lead&&!lead.contacted_at&&updates.stage!=='Accepted'&&updates.stage!=='Prospecting') payload.contacted_at=new Date().toISOString()
    }
    // Stamp won_at the first time a lead enters any won stage (Sold... / Completed Sale)
    if(updates.stage&&WON_STAGES.includes(updates.stage)){
      const lead=leads.find(l=>l.id===id)
      if(lead&&!lead.won_at) payload.won_at=new Date().toISOString()
    }
    if(updates.closed===true) payload.closed_at=new Date().toISOString()
    if(updates.closed===false){ payload.closed_at=null }
    const prev=leads.find(l=>l.id===id)
    const {data}=await supabase.from('leads').update(payload).eq('id',id).select().single()
    if(data) setLeads(p=>p.map(l=>l.id===id?data:l))
    // If assignment changed, notify the newly assigned rep (fire-and-forget)
    if(data&&updates.assigned_to&&updates.assigned_to!==(prev&&prev.assigned_to)){
      const assignedBy=email.split('@')[0]
      fetch(API_BASE+'/api/notify-assignment',{
        method:'POST',headers:{'Content-Type':'application/json'},
        body:JSON.stringify({lead_id:id,assigned_to:updates.assigned_to,assigned_by:assignedBy})
      }).catch(err=>console.warn('Assignment notify failed',err))
    }
  }

  async function addActivity(leadId,note,type='Note'){
    if(!note.trim()) return
    const by=email.split('@')[0]
    const {data}=await supabase.from('activities').insert([{lead_id:leadId,type,note,by,created_at:new Date().toISOString()}]).select().single()
    if(data) setActivities(p=>({...p,[leadId]:[data,...(p[leadId]||[])]}))
  }

  async function saveNewLead(form){
    const now=new Date().toISOString()
    const {data}=await supabase.from('leads').insert([{...form,created_at:now,updated_at:now,closed:false,stage:form.stage||'Accepted',pipeline:form.pipeline||'Sales',source:'Manual Entry'}]).select().single()
    if(data){
      setLeads(p=>[data,...p]);setShowNewLead(false)
      // Notify the assigned rep on a brand-new manual lead
      if(data.assigned_to){
        const assignedBy=email.split('@')[0]
        fetch(API_BASE+'/api/notify-assignment',{
          method:'POST',headers:{'Content-Type':'application/json'},
          body:JSON.stringify({lead_id:data.id,assigned_to:data.assigned_to,assigned_by:assignedBy})
        }).catch(err=>console.warn('Assignment notify failed',err))
      }
    }
  }

  // ── Column layout: save / reset / drag ──────────────────────
  function saveColumnLayout(){
    try{
      window.localStorage.setItem('ezfloorz_cols_'+email,JSON.stringify({visible:visibleCols,order:colOrder}))
      setSavedMsg('Layout saved')
      setTimeout(()=>setSavedMsg(''),2000)
    }catch(e){
      setSavedMsg('Could not save')
      setTimeout(()=>setSavedMsg(''),2000)
    }
  }
  function resetColumnLayout(){
    setVisibleCols(ALL_COLS.filter(c=>c.defaultVisible).map(c=>c.key))
    setColOrder(ALL_COLS.map(c=>c.key))
    try{ window.localStorage.removeItem('ezfloorz_cols_'+email) }catch(e){}
    setSavedMsg('Reset to default')
    setTimeout(()=>setSavedMsg(''),2000)
  }
  function handleDrop(targetKey){
    if(!dragCol||dragCol===targetKey){ setDragCol(null); setDragOverCol(null); return }
    const next=[...colOrder]
    const from=next.indexOf(dragCol)
    const to=next.indexOf(targetKey)
    if(from===-1||to===-1){ setDragCol(null); setDragOverCol(null); return }
    next.splice(from,1)
    next.splice(to,0,dragCol)
    setColOrder(next)
    setDragCol(null)
    setDragOverCol(null)
  }

  function renderCell(l,col){
    const td={padding:'7px 10px',borderBottom:'1px solid #e4e4e7',borderRight:'1px solid #e4e4e7',verticalAlign:'middle',whiteSpace:'nowrap',color:'#18181b',fontSize:12}
    switch(col){
      case 'created_at': return <td style={td}>{fmtDate(l.created_at)}</td>
      case 'updated_at': return <td style={td}>{fmtDate(l.updated_at)}</td>
      case 'pipeline':   return <td style={td}>{l.pipeline||'—'}</td>
      case 'assigned_to':return <td style={{...td,maxWidth:110,overflow:'hidden',textOverflow:'ellipsis'}}>{l.assigned_to||'—'}</td>
      case 'stage':{
        const c=STAGE_COLORS[l.stage]||{bg:'#f4f4f5',color:'#71717a'}
        return <td style={td}><span style={{display:'inline-flex',alignItems:'center',gap:4,padding:'2px 8px',borderRadius:20,fontSize:10,fontWeight:500,...c}}><span style={{width:6,height:6,borderRadius:'50%',background:c.color,display:'inline-block'}}></span>{l.stage}</span></td>
      }
      case 'task': return <td style={td}>{l.task||'—'}</td>
      case 'task_date':{
        const overdue=isPastDue(l.task_date), dueToday=isDueToday(l.task_date)
        return <td style={{...td,color:overdue?RED:dueToday?'#854f0b':'inherit',fontWeight:(overdue||dueToday)?600:400}}>{fmtDate(l.task_date)}</td>
      }
      case 'company':    return <td style={{...td,maxWidth:120,overflow:'hidden',textOverflow:'ellipsis'}}>{l.company||'—'}</td>
      case 'first_name': return <td style={td}>{l.first_name||'—'}</td>
      case 'last_name':  return <td style={td}>{l.last_name||'—'}</td>
      case 'email':      return <td style={{...td,maxWidth:150,overflow:'hidden',textOverflow:'ellipsis'}}>{l.email?<a href={`mailto:${l.email}`} onClick={e=>e.stopPropagation()} style={{color:'#185fa5',textDecoration:'none'}} title={`Email ${l.email}`}>{l.email}</a>:'—'}</td>
      case 'phone':      return <td style={td}>{l.phone?<a href={`tel:${String(l.phone).replace(/[^\d+]/g,'')}`} onClick={e=>e.stopPropagation()} style={{color:'#185fa5',textDecoration:'none'}} title={`Call ${l.phone}`}>{l.phone}</a>:'—'}</td>
      case 'city':       return <td style={td}>{l.city||'—'}</td>
      case 'how_heard':  return <td style={td}>{l.how_heard||'—'}</td>
      case 'quote_amount': return <td style={{...td,color:RED,fontWeight:500}}>{fmtMoney(l.quote_amount)}</td>
      case 'products':   return <td style={{...td,maxWidth:140,overflow:'hidden',textOverflow:'ellipsis'}}>{l.products||'—'}</td>
      case 'source':     return <td style={td}>{l.source||'—'}</td>
      case 'order_number': return <td style={td}>{l.order_number||'—'}</td>
      case 'timeToContact':{
        const tb=timeBadge(l.timeToContact,'contact')
        return <td style={td}>{tb?<span style={{display:'inline-block',padding:'2px 7px',borderRadius:20,fontSize:10,fontWeight:500,background:tb.bg,color:tb.color}}>{tb.label}</span>:'—'}</td>
      }
      case 'timeToClose':{
        const tb=timeBadge(l.timeToClose,'close')
        return <td style={td}>{tb?<span style={{display:'inline-block',padding:'2px 7px',borderRadius:20,fontSize:10,fontWeight:500,background:tb.bg,color:tb.color}}>{tb.label}</span>:'—'}</td>
      }
      default: return <td style={td}>—</td>
    }
  }

  // Filter cell under each header
  function renderFilterCell(col){
    const cellStyle={padding:'4px 6px',background:'#fff',borderBottom:'1px solid #e4e4e7',borderRight:'1px solid #e4e4e7'}
    const inputStyle={width:'100%',padding:'3px 5px',border:'1px solid #e4e4e7',borderRadius:4,fontSize:11,fontFamily:'Inter,sans-serif',outline:'none',background:'#fff'}
    const fv=colFilters[col.key]||''
    if(col.filterType==='none') return <th style={cellStyle}></th>
    if(col.filterType==='text') return <th style={cellStyle}><input style={inputStyle} placeholder="filter…" value={fv} onChange={e=>setColFilter(col.key,e.target.value)}/></th>
    if(col.filterType==='select'){
      let opts=[]
      if(col.key==='stage') opts=STAGES
      else if(col.key==='assigned_to') opts=REPS
      else if(col.key==='pipeline') opts=['Sales','B2B']
      else opts=[...new Set(leads.map(l=>l[col.key]).filter(Boolean))].sort()
      return <th style={cellStyle}><select style={inputStyle} value={fv} onChange={e=>setColFilter(col.key,e.target.value)}><option value="">All</option>{opts.map(o=><option key={o}>{o}</option>)}</select></th>
    }
    if(col.filterType==='created') return <th style={cellStyle}><select style={inputStyle} value={fv} onChange={e=>setColFilter(col.key,e.target.value)}>
      <option value="">All time</option><option value="week">Last week</option><option value="month">Last month</option><option value="3month">Last 3 months</option></select></th>
    if(col.filterType==='taskdate') return <th style={cellStyle}><select style={inputStyle} value={fv} onChange={e=>setColFilter(col.key,e.target.value)}>
      <option value="">All</option><option value="today">Due today</option><option value="overdue">Past due</option></select></th>
    return <th style={cellStyle}></th>
  }

  const shownCols=colOrder.map(k=>ALL_COLS.find(c=>c.key===k)).filter(c=>c&&visibleCols.includes(c.key))
  const activeFilterCount=Object.values(colFilters).filter(Boolean).length

  return(
    <div style={{display:'flex',flexDirection:'column',minHeight:'100vh',background:'#f4f4f5',fontFamily:'Inter,sans-serif'}}>
      {/* Topbar */}
      <div style={{background:RED,padding:'0 16px',height:48,display:'flex',alignItems:'center',justifyContent:'space-between',flexShrink:0}}>
        <div style={{color:'#fff',fontSize:16,fontWeight:600}}>EZ Floorz <span style={{opacity:.7,fontSize:12,fontWeight:400,marginLeft:6}}>CRM</span></div>
        <div style={{color:'rgba(255,255,255,.8)',fontSize:12,display:'flex',alignItems:'center',gap:8}}>
          <span>{email}</span>
          <button style={{background:'rgba(255,255,255,.15)',border:'none',color:'#fff',padding:'4px 10px',borderRadius:4,fontSize:12,cursor:'pointer'}} onClick={()=>supabase.auth.signOut()}>Sign out</button>
        </div>
      </div>

      {/* Toolbar */}
      <div style={{background:'#fff',borderBottom:'1px solid #e4e4e7',padding:'8px 14px',display:'flex',alignItems:'center',gap:8,flexWrap:'wrap'}}>
        <span style={{fontSize:14,fontWeight:600,marginRight:4}}>{colFilters.assigned_to?colFilters.assigned_to.split(' ')[0]+"'s Leads":view==='open'?'Open Leads':view==='closed'?'Closed / Won':'All Leads'}</span>
        <span style={{fontSize:11,color:'#71717a'}}>{visibleLeads.length} of {leads.length}</span>
        <div style={{display:'flex',border:'1px solid #e4e4e7',borderRadius:6,overflow:'hidden'}}>
          {['open','closed','all'].map(v=>(
            <button key={v} style={{padding:'5px 12px',fontSize:12,fontWeight:500,cursor:'pointer',background:view===v?RED:'#fff',color:view===v?'#fff':'#52525b',border:'none',fontFamily:'Inter,sans-serif'}} onClick={()=>setView(v)}>
              {v.charAt(0).toUpperCase()+v.slice(1)}
            </button>
          ))}
        </div>
        <input style={{padding:'5px 10px',border:'1px solid #e4e4e7',borderRadius:6,fontSize:12,fontFamily:'Inter,sans-serif',width:200,outline:'none'}}
          placeholder="Search name, email, company…" value={searchQ} onChange={e=>setSearchQ(e.target.value)}/>
        <button style={{padding:'5px 10px',fontSize:12,fontWeight:500,cursor:'pointer',fontFamily:'Inter,sans-serif',
          background:'#fff',color:email?RED:'#18181b',border:`1px solid ${RED}`,borderRadius:6}}
          onClick={()=>{const u=email.split('@')[0].toLowerCase();const map={nick:'Nick Olberding',tyler:'Tyler Ploog',nicole:'Nicole Ahrendt',hailey:'Hailey Wade'};setColFilter('assigned_to',map[u]||'')}}>
          My Leads
        </button>
        {activeFilterCount>0&&<button style={{padding:'5px 10px',borderRadius:6,fontSize:12,cursor:'pointer',fontFamily:'Inter,sans-serif',background:'#fff',color:'#71717a',border:'1px solid #e4e4e7'}}
          onClick={clearColFilters}>Clear filters ({activeFilterCount})</button>}
        <div style={{flex:1}}/>
        <div style={{position:'relative'}}>
          <button style={{padding:'5px 12px',borderRadius:6,fontSize:12,fontWeight:500,cursor:'pointer',fontFamily:'Inter,sans-serif',background:'#fff',color:'#18181b',border:'1px solid #e4e4e7'}}
            onClick={()=>setShowColPicker(p=>!p)}>Columns ▾</button>
          {showColPicker&&<ColPicker visible={visibleCols} onChange={setVisibleCols} onClose={()=>setShowColPicker(false)} order={colOrder} onSave={saveColumnLayout} onReset={resetColumnLayout} savedMsg={savedMsg}/>}
        </div>
        <button style={{padding:'5px 12px',borderRadius:6,fontSize:12,fontWeight:500,cursor:'pointer',fontFamily:'Inter,sans-serif',background:RED,color:'#fff',border:`1px solid ${RED}`}}
          onClick={()=>setShowNewLead(true)}>+ New Lead</button>
      </div>

      {/* KPIs */}
      {anyFilterActive&&(
        <div style={{background:'#fdf0f2',borderBottom:'1px solid #f5c6cf',padding:'5px 14px',fontSize:11,color:RED,fontWeight:500}}>
          {colFilters.assigned_to?`Showing performance stats for ${colFilters.assigned_to}`:'Stats reflect current filters'} · {kpiBase.length} leads
        </div>
      )}
      <div style={{display:'grid',gridTemplateColumns:'repeat(6,1fr)',borderBottom:'1px solid #e4e4e7',background:'#fff'}}>
        {[
          {label:'Open Leads',         val:openLeads.length,                        sub:'Active',          accent:true},
          {label:'Pipeline Value',     val:'$'+pipelineVal.toLocaleString(),         sub:'Quoted open'},
          {label:'Closed Won (12mo)',   val:'$'+wonVal.toLocaleString(),              sub:wonLast12.length+' sold · last 12 months'},
          {label:'Avg Time to Contact',val:avg(ttcArr)!==null?avg(ttcArr)+'d':'—',   sub:'Days from lead in'},
          {label:'Avg Time to Close',  val:avg(ttclArr)!==null?avg(ttclArr)+'d':'—', sub:'Days from lead in'},
          {label:'Overdue Tasks',      val:overdueCount,                            sub:'Past task date',  urgent:overdueCount>0},
        ].map((k,i)=>(
          <div key={i} style={{padding:'10px 14px',borderRight:i<5?'1px solid #e4e4e7':'none'}}>
            <div style={{fontSize:10,color:'#71717a',textTransform:'uppercase',letterSpacing:.5,marginBottom:3}}>{k.label}</div>
            <div style={{fontSize:20,fontWeight:600,color:(k.accent||k.urgent)?RED:'#18181b'}}>{k.val}</div>
            <div style={{fontSize:10,color:'#a1a1aa',marginTop:1}}>{k.sub}</div>
          </div>
        ))}
      </div>

      {/* Table */}
      <div style={{overflowX:'auto',flex:1}}>
        <table style={{width:'100%',borderCollapse:'collapse',fontSize:12}}>
          <thead>
            <tr>
              {shownCols.map(c=>(
                <th key={c.key}
                  draggable
                  onDragStart={()=>setDragCol(c.key)}
                  onDragOver={e=>{e.preventDefault();setDragOverCol(c.key)}}
                  onDragLeave={()=>setDragOverCol(null)}
                  onDrop={e=>{e.preventDefault();handleDrop(c.key)}}
                  onDragEnd={()=>{setDragCol(null);setDragOverCol(null)}}
                  style={{position:'sticky',top:0,
                    background:dragOverCol===c.key?'#fdf0f2':'#fafafa',
                    padding:'7px 10px',textAlign:'left',fontSize:10,fontWeight:500,
                    color:'#71717a',borderBottom:'1px solid #e4e4e7',
                    borderRight:'1px solid #e4e4e7',
                    borderLeft:dragOverCol===c.key?`2px solid ${RED}`:'none',
                    whiteSpace:'nowrap',userSelect:'none',zIndex:10,
                    opacity:dragCol===c.key?.4:1,cursor:'grab'}}
                  title="Drag to reorder · click label to sort">
                  <span style={{opacity:.35,marginRight:4,cursor:'grab'}}>⠿</span>
                  <span onClick={()=>c.sortable&&handleSort(c.key)} style={{cursor:c.sortable?'pointer':'default'}}>
                    {c.label}{c.sortable&&sortArrow(c.key)}
                  </span>
                </th>
              ))}
            </tr>
            <tr>{shownCols.map(c=><React.Fragment key={c.key}>{renderFilterCell(c)}</React.Fragment>)}</tr>
          </thead>
          <tbody>
            {visibleLeads.map(l=>(
              <tr key={l.id} onClick={()=>setActiveId(l.id)} style={{cursor:'pointer'}}
                onMouseEnter={e=>e.currentTarget.style.background='#fafafa'}
                onMouseLeave={e=>e.currentTarget.style.background='transparent'}>
                {shownCols.map(c=><React.Fragment key={c.key}>{renderCell(l,c.key)}</React.Fragment>)}
              </tr>
            ))}
            {visibleLeads.length===0&&(
              <tr><td colSpan={shownCols.length} style={{padding:32,textAlign:'center',color:'#71717a',fontSize:13}}>No leads match your filters</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Slide-over */}
      {activeId&&activeLead&&(
        <>
          <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,.35)',zIndex:100}} onClick={()=>setActiveId(null)}/>
          <div style={{position:'fixed',top:0,right:0,width:480,height:'100%',background:'#fff',borderLeft:'1px solid #e4e4e7',zIndex:101,display:'flex',flexDirection:'column',overflowY:'auto'}}>
            <div style={{background:RED,padding:'16px 20px',display:'flex',alignItems:'flex-start',justifyContent:'space-between'}}>
              <div>
                <div style={{color:'#fff',fontSize:17,fontWeight:600}}>{activeLead.first_name} {activeLead.last_name}</div>
                <div style={{color:'rgba(255,255,255,.75)',fontSize:12,marginTop:2}}>{activeLead.pipeline||'Sales'} · {activeLead.assigned_to}</div>
              </div>
              <button style={{background:'none',border:'none',color:'#fff',fontSize:22,cursor:'pointer',lineHeight:1,padding:0}} onClick={()=>setActiveId(null)}>×</button>
            </div>
            <div style={{flex:1,padding:16,display:'flex',flexDirection:'column',gap:14}}>
              <div>
                <div style={{fontSize:10,fontWeight:600,color:'#71717a',textTransform:'uppercase',letterSpacing:.6,marginBottom:8}}>Contact Info</div>
                <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:8}}>
                  {[['Phone',activeLead.phone],['Email',activeLead.email],['City',activeLead.city],['Company',activeLead.company],['How Heard',activeLead.how_heard],['Source',activeLead.source]].map(([l,v])=>(
                    <div key={l} style={{background:'#fafafa',borderRadius:6,padding:'8px 10px',border:'1px solid #e4e4e7'}}>
                      <div style={{fontSize:10,color:'#71717a',marginBottom:2}}>{l}</div>
                      <div style={{fontSize:13,fontWeight:500,overflow:'hidden',textOverflow:'ellipsis'}}>
                        {v?(l==='Email'?<a href={`mailto:${v}`} style={{color:'#185fa5',textDecoration:'none'}}>{v}</a>
                          :l==='Phone'?<a href={`tel:${String(v).replace(/[^\d+]/g,'')}`} style={{color:'#185fa5',textDecoration:'none'}}>{v}</a>
                          :v):'—'}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <div>
                <div style={{fontSize:10,fontWeight:600,color:'#71717a',textTransform:'uppercase',letterSpacing:.6,marginBottom:8}}>Deal Info</div>
                <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:8}}>
                  {[
                    ['Quote Amount',fmtMoney(activeLead.quote_amount)],
                    ['Products',activeLead.products],
                    ['Sq Ft',activeLead.sqft],
                    ['Order #',activeLead.order_number],
                    ['Time to Contact',activeLead.contacted_at?daysDiff(activeLead.created_at,activeLead.contacted_at)+'d':'—'],
                    ['Time to Close',activeLead.closed_at?daysDiff(activeLead.created_at,activeLead.closed_at)+'d':'—'],
                  ].map(([l,v])=>(
                    <div key={l} style={{background:'#fafafa',borderRadius:6,padding:'8px 10px',border:'1px solid #e4e4e7'}}>
                      <div style={{fontSize:10,color:'#71717a',marginBottom:2}}>{l}</div>
                      <div style={{fontSize:13,fontWeight:500,color:l==='Quote Amount'?RED:'inherit'}}>{v||'—'}</div>
                    </div>
                  ))}
                  <div style={{gridColumn:'1/-1',background:'#fafafa',borderRadius:6,padding:'8px 10px',border:'1px solid #e4e4e7'}}>
                    <div style={{fontSize:10,color:'#71717a',marginBottom:2}}>Notes</div>
                    <div style={{fontSize:13,fontWeight:500}}>{activeLead.notes||'—'}</div>
                  </div>
                </div>
              </div>
              <div>
                <div style={{fontSize:10,fontWeight:600,color:'#71717a',textTransform:'uppercase',letterSpacing:.6,marginBottom:8}}>Update</div>
                <div style={{display:'flex',flexDirection:'column',gap:8}}>
                  <div style={{display:'flex',gap:8}}>
                    <select style={{flex:1,padding:'7px 8px',border:'1px solid #e4e4e7',borderRadius:6,fontSize:12,fontFamily:'Inter,sans-serif',background:'#fff',outline:'none'}} value={activeLead.stage||''} onChange={e=>updateLead(activeId,{stage:e.target.value})}>
                      {STAGES.map(s=><option key={s}>{s}</option>)}
                    </select>
                    <select style={{flex:1,padding:'7px 8px',border:'1px solid #e4e4e7',borderRadius:6,fontSize:12,fontFamily:'Inter,sans-serif',background:'#fff',outline:'none'}} value={activeLead.assigned_to||''} onChange={e=>updateLead(activeId,{assigned_to:e.target.value})}>
                      {REPS.map(r=><option key={r}>{r}</option>)}
                    </select>
                  </div>
                  <div style={{display:'flex',gap:8}}>
                    <input style={{flex:1,padding:'7px 8px',border:'1px solid #e4e4e7',borderRadius:6,fontSize:12,fontFamily:'Inter,sans-serif',outline:'none'}} placeholder="Quote amount" type="number" defaultValue={activeLead.quote_amount||''}
                      onBlur={e=>e.target.value&&updateLead(activeId,{quote_amount:parseFloat(e.target.value)})}/>
                    <input style={{flex:1,padding:'7px 8px',border:'1px solid #e4e4e7',borderRadius:6,fontSize:12,fontFamily:'Inter,sans-serif',outline:'none'}} placeholder="Order #" defaultValue={activeLead.order_number||''}
                      onBlur={e=>updateLead(activeId,{order_number:e.target.value})}/>
                  </div>
                  <input style={{width:'100%',padding:'7px 8px',border:'1px solid #e4e4e7',borderRadius:6,fontSize:12,fontFamily:'Inter,sans-serif',outline:'none'}} placeholder="Task date" type="date" defaultValue={activeLead.task_date?activeLead.task_date.split('T')[0]:''}
                    onBlur={e=>updateLead(activeId,{task_date:e.target.value})}/>
                </div>
              </div>
              <div>
                <div style={{fontSize:10,fontWeight:600,color:'#71717a',textTransform:'uppercase',letterSpacing:.6,marginBottom:8}}>Activity Log</div>
                {(activities[activeId]||[]).length===0
                  ?<div style={{fontSize:12,color:'#71717a'}}>No activity yet</div>
                  :(activities[activeId]||[]).map(a=>(
                    <div key={a.id} style={{display:'flex',gap:10,paddingBottom:10,borderBottom:'1px solid #f4f4f5'}}>
                      <div style={{width:7,height:7,borderRadius:'50%',background:RED,marginTop:5,flexShrink:0}}/>
                      <div style={{flex:1}}>
                        <div style={{fontSize:12,fontWeight:500}}>{a.type} <span style={{fontWeight:400,color:'#71717a'}}>by {a.by}</span></div>
                        <div style={{fontSize:11,color:'#71717a'}}>{a.note} · {fmtDate(a.created_at)}</div>
                      </div>
                    </div>
                  ))
                }
                <LogNote onSave={note=>addActivity(activeId,note)}/>
              </div>
            </div>
            <div style={{padding:'12px 16px',borderTop:'1px solid #e4e4e7',background:'#fff'}}>
              {(activeLead.closed||CLOSED_STAGES.includes(activeLead.stage)||activeLead.unqualified)?(
                /* CLOSED lead — completed sale, no sale, dead */
                <div>
                  {activeLead.lost_reason&&<div style={{fontSize:11,color:'#71717a',marginBottom:8}}>{activeLead.unqualified?'Unqualified':'Lost'} reason: <strong style={{color:'#18181b'}}>{activeLead.lost_reason}</strong></div>}
                  <button style={{width:'100%',padding:'8px',background:'#fff',color:'#18181b',border:'1px solid #e4e4e7',borderRadius:6,fontSize:13,fontWeight:500,cursor:'pointer'}} onClick={()=>{updateLead(activeId,{closed:false,unqualified:false,lost_reason:null,stage:'Accepted',closed_at:null});setActiveId(null)}}>Reopen Lead</button>
                </div>
              ):WON_STAGES.includes(activeLead.stage)?(
                /* WON but still OPEN — in a Sold stage, job ongoing */
                <div style={{display:'flex',flexDirection:'column',gap:8}}>
                  <div style={{fontSize:11,color:'#0f6e56',fontWeight:500,marginBottom:2}}>✓ Won — job in progress. Mark completed when the job is finished.</div>
                  <button style={{width:'100%',padding:'8px',background:'#0a5544',color:'#fff',border:'none',borderRadius:6,fontSize:13,fontWeight:500,cursor:'pointer'}} onClick={()=>{updateLead(activeId,{closed:true,stage:'Completed Sale'});setActiveId(null)}}>Mark Completed ✓</button>
                  <button style={{width:'100%',padding:'8px',background:'#fff',color:'#791f1f',border:'1px solid #f5c6cf',borderRadius:6,fontSize:13,fontWeight:500,cursor:'pointer'}} onClick={()=>setCloseReasonMode('lost')}>Mark Lost</button>
                </div>
              ):(
                /* ACTIVE lead */
                <div style={{display:'flex',flexDirection:'column',gap:8}}>
                  <button style={{width:'100%',padding:'8px',background:'#0f6e56',color:'#fff',border:'none',borderRadius:6,fontSize:13,fontWeight:500,cursor:'pointer'}} onClick={()=>{updateLead(activeId,{stage:'Sold - Materials on Order',unqualified:false});setActiveId(null)}}>Mark as Won ✓</button>
                  <div style={{display:'flex',gap:8}}>
                    <button style={{flex:1,padding:'8px',background:'#fff',color:'#791f1f',border:'1px solid #f5c6cf',borderRadius:6,fontSize:13,fontWeight:500,cursor:'pointer'}} onClick={()=>setCloseReasonMode('lost')}>Mark Lost</button>
                    <button style={{flex:1,padding:'8px',background:'#fff',color:'#71717a',border:'1px solid #e4e4e7',borderRadius:6,fontSize:13,fontWeight:500,cursor:'pointer'}} onClick={()=>setCloseReasonMode('unqualified')}>Mark Unqualified</button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </>
      )}

      {/* Close-reason picker (Lost / Unqualified) */}
      {closeReasonMode&&activeLead&&(
        <ReasonModal
          mode={closeReasonMode}
          onClose={()=>setCloseReasonMode(null)}
          onConfirm={(reason)=>{
            if(closeReasonMode==='lost'){
              updateLead(activeId,{closed:true,stage:'No Sale',lost_reason:reason,unqualified:false})
            }else{
              updateLead(activeId,{closed:true,stage:'Dead',lost_reason:reason,unqualified:true})
            }
            setCloseReasonMode(null)
            setActiveId(null)
          }}
        />
      )}

      {showNewLead&&<NewLeadModal onSave={saveNewLead} onClose={()=>setShowNewLead(false)}/>}
    </div>
  )
}

function LogNote({onSave}){
  const [val,setVal]=useState('')
  return(
    <div style={{display:'flex',gap:6,marginTop:10}}>
      <input style={{flex:1,padding:'6px 8px',border:'1px solid #e4e4e7',borderRadius:6,fontSize:12,fontFamily:'Inter,sans-serif',outline:'none'}}
        placeholder="Log a call, note, or update…" value={val} onChange={e=>setVal(e.target.value)}
        onKeyDown={e=>{if(e.key==='Enter'&&val.trim()){onSave(val);setVal('')}}}/>
      <button style={{padding:'6px 12px',background:RED,color:'#fff',border:'none',borderRadius:6,fontSize:12,cursor:'pointer',fontFamily:'Inter,sans-serif'}}
        onClick={()=>{if(val.trim()){onSave(val);setVal('')}}}>Log</button>
    </div>
  )
}

function ReasonModal({mode,onConfirm,onClose}){
  const [reason,setReason]=useState('')
  const isLost=mode==='lost'
  const reasons=isLost?LOST_REASONS:UNQUALIFIED_REASONS
  const title=isLost?'Mark Lead Lost':'Mark Lead Unqualified'
  const accent=isLost?'#791f1f':'#71717a'
  const note=isLost
    ?'This lead will move to No Sale. A reason is required.'
    :'This lead will move to Dead and be excluded from performance stats. A reason is required.'
  return(
    <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,.4)',zIndex:300,display:'flex',alignItems:'center',justifyContent:'center'}}>
      <div style={{background:'#fff',borderRadius:10,width:420,border:'1px solid #e4e4e7',overflow:'hidden'}}>
        <div style={{background:accent,padding:'16px 20px',display:'flex',justifyContent:'space-between',alignItems:'center'}}>
          <span style={{color:'#fff',fontSize:15,fontWeight:600}}>{title}</span>
          <button style={{background:'none',border:'none',color:'#fff',fontSize:22,cursor:'pointer',lineHeight:1,padding:0}} onClick={onClose}>×</button>
        </div>
        <div style={{padding:20}}>
          <div style={{fontSize:12,color:'#71717a',marginBottom:12,lineHeight:1.5}}>{note}</div>
          <label style={{fontSize:11,fontWeight:600,color:'#52525b',display:'block',marginBottom:6,textTransform:'uppercase',letterSpacing:.5}}>{isLost?'Lost Reason':'Unqualified Reason'}</label>
          <select autoFocus style={{width:'100%',padding:'8px 10px',border:'1px solid #e4e4e7',borderRadius:6,fontSize:13,fontFamily:'Inter,sans-serif',background:'#fff',outline:'none'}}
            value={reason} onChange={e=>setReason(e.target.value)}>
            <option value="">Select a reason…</option>
            {reasons.map(r=><option key={r}>{r}</option>)}
          </select>
        </div>
        <div style={{padding:'12px 20px',borderTop:'1px solid #e4e4e7',display:'flex',justifyContent:'flex-end',gap:8}}>
          <button style={{padding:'7px 14px',borderRadius:6,fontSize:12,fontWeight:500,cursor:'pointer',fontFamily:'Inter,sans-serif',background:'#fff',color:'#18181b',border:'1px solid #e4e4e7'}} onClick={onClose}>Cancel</button>
          <button disabled={!reason} style={{padding:'7px 14px',borderRadius:6,fontSize:12,fontWeight:500,cursor:reason?'pointer':'not-allowed',fontFamily:'Inter,sans-serif',background:reason?accent:'#e4e4e7',color:'#fff',border:'none'}}
            onClick={()=>reason&&onConfirm(reason)}>Confirm</button>
        </div>
      </div>
    </div>
  )
}

function NewLeadModal({onSave,onClose}){
  const [f,setF]=useState({pipeline:'Sales',stage:'Accepted',assigned_to:'Nick Olberding',task:'Follow Up'})
  const set=(k,v)=>setF(p=>({...p,[k]:v}))
  const inp=(key,placeholder,type='text')=>(
    <input style={{padding:'7px 10px',border:'1px solid #e4e4e7',borderRadius:6,fontSize:12,fontFamily:'Inter,sans-serif',outline:'none',width:'100%'}}
      type={type} placeholder={placeholder} value={f[key]||''} onChange={e=>set(key,e.target.value)}/>
  )
  const sel=(key,opts)=>(
    <select style={{padding:'7px 10px',border:'1px solid #e4e4e7',borderRadius:6,fontSize:12,fontFamily:'Inter,sans-serif',background:'#fff',outline:'none',width:'100%'}}
      value={f[key]||''} onChange={e=>set(key,e.target.value)}>
      {opts.map(o=><option key={o}>{o}</option>)}
    </select>
  )
  return(
    <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,.4)',zIndex:200,display:'flex',alignItems:'center',justifyContent:'center'}}>
      <div style={{background:'#fff',borderRadius:10,width:520,maxHeight:'90vh',overflowY:'auto',border:'1px solid #e4e4e7'}}>
        <div style={{background:RED,padding:'16px 20px',borderRadius:'10px 10px 0 0',display:'flex',justifyContent:'space-between',alignItems:'center'}}>
          <span style={{color:'#fff',fontSize:15,fontWeight:600}}>Add New Lead</span>
          <button style={{background:'none',border:'none',color:'#fff',fontSize:22,cursor:'pointer',lineHeight:1,padding:0}} onClick={onClose}>×</button>
        </div>
        <div style={{padding:20,display:'flex',flexDirection:'column',gap:12}}>
          {[['First name','first_name','Last name','last_name'],['Phone','phone','Email','email'],['City','city','Company','company']].map((row,i)=>(
            <div key={i} style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:10}}>
              <div><label style={{fontSize:11,fontWeight:500,color:'#52525b',display:'block',marginBottom:4}}>{row[0]}</label>{inp(row[1],row[0],row[0]==='Phone'?'tel':row[0]==='Email'?'email':'text')}</div>
              <div><label style={{fontSize:11,fontWeight:500,color:'#52525b',display:'block',marginBottom:4}}>{row[2]}</label>{inp(row[3],row[2])}</div>
            </div>
          ))}
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:10}}>
            <div><label style={{fontSize:11,fontWeight:500,color:'#52525b',display:'block',marginBottom:4}}>Pipeline</label>{sel('pipeline',['Sales','B2B'])}</div>
            <div><label style={{fontSize:11,fontWeight:500,color:'#52525b',display:'block',marginBottom:4}}>Assigned to</label>{sel('assigned_to',REPS)}</div>
          </div>
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:10}}>
            <div><label style={{fontSize:11,fontWeight:500,color:'#52525b',display:'block',marginBottom:4}}>Stage</label>{sel('stage',STAGES)}</div>
            <div><label style={{fontSize:11,fontWeight:500,color:'#52525b',display:'block',marginBottom:4}}>How heard</label>{sel('how_heard',[''].concat(SOURCES))}</div>
          </div>
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:10}}>
            <div><label style={{fontSize:11,fontWeight:500,color:'#52525b',display:'block',marginBottom:4}}>Products</label>{inp('products','carpet, LVP…')}</div>
            <div><label style={{fontSize:11,fontWeight:500,color:'#52525b',display:'block',marginBottom:4}}>Quote amount</label>{inp('quote_amount','$0','number')}</div>
          </div>
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:10}}>
            <div><label style={{fontSize:11,fontWeight:500,color:'#52525b',display:'block',marginBottom:4}}>Task</label>{inp('task','Follow Up')}</div>
            <div><label style={{fontSize:11,fontWeight:500,color:'#52525b',display:'block',marginBottom:4}}>Task date</label>{inp('task_date','','date')}</div>
          </div>
          <div>
            <label style={{fontSize:11,fontWeight:500,color:'#52525b',display:'block',marginBottom:4}}>Notes</label>
            <textarea style={{padding:'7px 10px',border:'1px solid #e4e4e7',borderRadius:6,fontSize:12,fontFamily:'Inter,sans-serif',outline:'none',width:'100%',minHeight:60,resize:'vertical'}}
              placeholder="Any details…" value={f.notes||''} onChange={e=>set('notes',e.target.value)}/>
          </div>
        </div>
        <div style={{padding:'12px 20px',borderTop:'1px solid #e4e4e7',display:'flex',justifyContent:'flex-end',gap:8}}>
          <button style={{padding:'7px 14px',borderRadius:6,fontSize:12,fontWeight:500,cursor:'pointer',fontFamily:'Inter,sans-serif',background:'#fff',color:'#18181b',border:'1px solid #e4e4e7'}} onClick={onClose}>Cancel</button>
          <button style={{padding:'7px 14px',borderRadius:6,fontSize:12,fontWeight:500,cursor:'pointer',fontFamily:'Inter,sans-serif',background:RED,color:'#fff',border:`1px solid ${RED}`}} onClick={()=>onSave(f)}>Add Lead</button>
        </div>
      </div>
    </div>
  )
}
